import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { MatDialogRef } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { combineLatest, Subscription } from 'rxjs';

import {
  AddressViewModel,
  SiteContextListResponse,
  SiteContextUpdateRequest,
} from '@app/api/marketplace/models';
import { UserService } from '@authentication/services/user.service';
import {
  AuthenticationActions,
  AuthenticationSelectors,
} from '@authentication/store/authentication.slice';
import { MerchantSelectors } from '@merchant/store/merchant.slice';

@Component({
  selector: 'app-context-switcher',
  templateUrl: './context-switcher.component.html',
  styleUrls: ['./context-switcher.component.scss'],
})
export class ContextSwitcherComponent implements OnInit, OnDestroy {
  @ViewChildren('chip', { read: ElementRef }) chips!: QueryList<
    ElementRef<HTMLElement>
  >;

  subs = new Subscription();
  contexts: Array<SiteContextListResponse & { name?: string }> = [];
  selectedOption: SiteContextUpdateRequest & { name: string } = {
    siteId: '',
    merchantType: 'RETAILER_AS_OTHER_RETAILER',
    name: '',
  };

  maxWidth = 0;

  isBuying = true;

  choosableContexts?: Array<
    SiteContextListResponse & { address: AddressViewModel }
  >;

  ngOnInit() {
    this.isBuying = this.router.url.includes('buying');

    this.subs.add(
      this.router.events.subscribe((e) => {
        if (
          e instanceof NavigationEnd &&
          e.url.includes('admin/impersonation')
        ) {
          this.dialogRef.close();
        }
      })
    );

    this.subs.add(
      combineLatest([
        this.store.select(AuthenticationSelectors.selectChoosableContexts),
        this.store.select(MerchantSelectors.selectSiteList),
        this.store.select(AuthenticationSelectors.selectUserInfo),
      ]).subscribe(([contexts, sites, sess]) => {
        const selectedSiteId = sess.attributes?.['custom:selectedSiteId'];
        this.contexts = contexts.map((context) => ({
          ...context,
          name: sites.find((site) => site.id === context.siteId)?.name || '',
        }));
        if (selectedSiteId) {
          const selectedSite = this.contexts.find(
            (s) => s.siteId === selectedSiteId
          );
          this.selectedOption.siteId = selectedSite?.siteId || '';
          this.selectedOption.name = selectedSite?.name || '';
          this.selectedOption.merchantType =
            sess.attributes?.['custom:selectedMerchantType'];
        }

        this.choosableContexts = contexts.map((context) => ({
          ...context,
          address: {
            ...sites.find((site) => site.id === context.siteId)?.address!,
          },
        }));
      })
    );

    setTimeout(() => {
      this.maxWidth = Math.max(
        ...this.chips.map((o) => o.nativeElement.offsetWidth)
      );
    }, 0);
  }

  constructor(
    private userService: UserService,
    private store: Store,
    public readonly dialogRef: MatDialogRef<ContextSwitcherComponent>,
    private router: Router
  ) {}

  selectedContextChanged(event: MatButtonToggleChange) {
    this.userService.switchContext(event.value);
    this.store.dispatch(
      AuthenticationActions.toggleOpenedSwitcher.trigger({
        opened: false,
      })
    );
    setTimeout(() => {
      this.dialogRef.close({ selected: true });
    }, 500);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
